/**
 * Roblox BrickColors, curated.
 *
 * Designers match garments to named Roblox colours constantly — a shirt has to
 * sit against a build, a uniform against a group's palette. No general image
 * editor has these, and offering them is the same instinct as calling
 * `torso.front` "Chest": speak the audience's vocabulary rather than making
 * them translate.
 *
 * **Curated, not complete.** The full palette is ~190 entries, which is a wall
 * rather than a palette. These are the ones that actually appear in clothing
 * work, grouped so a colour can be found by looking rather than by reading.
 *
 * Two deliberate exclusions:
 *
 *   - **Transparent and "Tr." colours.** They describe material transparency on
 *     a part, not a colour to paint with. Ours is decided by the alpha channel.
 *   - **Names that map to two different colours** — Gold, Rust, Lilac and Deep
 *     orange each exist twice in the real palette at different ids. Shipping a
 *     label that means two things is worse than omitting it, because the whole
 *     point of the feature is that the name is trustworthy.
 *
 * Values verified against a BrickColor reference table rather than recalled.
 * Getting one wrong is invisible here and obvious on an avatar standing next to
 * the build it was meant to match.
 */
import type { Hex } from "./format";

export interface BrickColor {
  name: string;
  hex: Hex;
}

export interface BrickColorGroup {
  name: string;
  colours: readonly BrickColor[];
}

export const BRICKCOLOR_GROUPS: readonly BrickColorGroup[] = [
  {
    name: "Neutrals",
    colours: [
      { name: "Institutional white", hex: "#F8F8F8" },
      { name: "White", hex: "#F2F3F3" },
      { name: "Light stone grey", hex: "#E5E4DF" },
      { name: "Mid gray", hex: "#CDCDCD" },
      { name: "Medium stone grey", hex: "#A3A2A5" },
      { name: "Dark stone grey", hex: "#635F62" },
      { name: "Black", hex: "#1B2A35" },
      { name: "Really black", hex: "#111111" },
    ],
  },
  {
    name: "Reds & pinks",
    colours: [
      { name: "Really red", hex: "#FF0000" },
      { name: "Bright red", hex: "#C4281C" },
      { name: "Crimson", hex: "#970000" },
      { name: "Maroon", hex: "#750000" },
      { name: "Salmon", hex: "#FF9494" },
      { name: "Pink", hex: "#FF66CC" },
      { name: "Hot pink", hex: "#FF00BF" },
      { name: "Carnation pink", hex: "#FF98DC" },
    ],
  },
  {
    name: "Oranges & yellows",
    colours: [
      { name: "Bright orange", hex: "#DA8541" },
      { name: "Neon orange", hex: "#D5733D" },
      { name: "Br. yellowish orange", hex: "#E29B40" },
      { name: "Bright yellow", hex: "#F5CD30" },
      { name: "New Yeller", hex: "#FFFF00" },
      { name: "Cool yellow", hex: "#FDEA8D" },
      { name: "Daisy orange", hex: "#F8D96D" },
      { name: "Curry", hex: "#D7A94B" },
    ],
  },
  {
    name: "Greens",
    colours: [
      { name: "Lime green", hex: "#00FF00" },
      { name: "Bright green", hex: "#4B974B" },
      { name: "Dark green", hex: "#287F47" },
      { name: "Forest green", hex: "#1F801D" },
      { name: "Sea green", hex: "#348E40" },
      { name: "Camo", hex: "#3A7D15" },
      { name: "Slime green", hex: "#506D54" },
      { name: "Mint", hex: "#B1E5A6" },
    ],
  },
  {
    name: "Blues",
    colours: [
      { name: "Really blue", hex: "#0000FF" },
      { name: "Bright blue", hex: "#0D69AC" },
      { name: "Deep blue", hex: "#2154B9" },
      { name: "Navy blue", hex: "#002060" },
      { name: "Electric blue", hex: "#0989CF" },
      { name: "Steel blue", hex: "#527CAE" },
      { name: "Cyan", hex: "#04AFEC" },
      { name: "Toothpaste", hex: "#00FFFF" },
    ],
  },
  {
    name: "Purples",
    colours: [
      { name: "Bright violet", hex: "#6B327C" },
      { name: "Royal purple", hex: "#6225D1" },
      { name: "Magenta", hex: "#AA00AA" },
      { name: "Eggplant", hex: "#7B007B" },
      { name: "Lavender", hex: "#8C5B9F" },
      { name: "Alder", hex: "#B480FF" },
      { name: "Plum", hex: "#7B2F7B" },
      { name: "Pastel violet", hex: "#B1A7FF" },
    ],
  },
  {
    name: "Browns & tans",
    colours: [
      { name: "Reddish brown", hex: "#694028" },
      { name: "Brown", hex: "#7C5C46" },
      { name: "Dirt brown", hex: "#564236" },
      { name: "Nougat", hex: "#CC8E69" },
      { name: "Brick yellow", hex: "#D7C59A" },
      { name: "Cashmere", hex: "#D3BE96" },
      { name: "Burlap", hex: "#C7AC78" },
      { name: "Burgundy", hex: "#883E3E" },
    ],
  },
] as const;

/** Flat list, for lookup and for tests. */
export const BRICKCOLORS: readonly BrickColor[] = BRICKCOLOR_GROUPS.flatMap((g) => g.colours);

/** The Roblox name for an exact hex, if there is one. Used to label a swatch. */
export function brickColorName(hex: string): string | null {
  const target = hex.trim().toUpperCase();
  return BRICKCOLORS.find((c) => c.hex.toUpperCase() === target)?.name ?? null;
}
